import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Trash2, Plus, ExternalLink, Upload, RefreshCw, Loader2, Fingerprint } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import VoiceFingerprint from "@/components/VoiceFingerprint";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [styles, setStyles] = useState([]);
  const [newStyle, setNewStyle] = useState({ name: "", description: "", sample_text: "" });
  const [saving, setSaving] = useState(false);
  const [building, setBuilding] = useState(false);
  const [rebuilding, setRebuilding] = useState("");
  const sampleFileRef = useRef(null);

  useEffect(() => { setName(user?.name || ""); }, [user]);
  useEffect(() => { api.get("/styles").then(({ data }) => setStyles(data)); }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/me", { name });
      await refresh();
      toast.success("Profile updated");
    } catch { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  const addStyle = async () => {
    if (newStyle.sample_text.trim().split(/\s+/).length < 20) {
      toast.error("Sample text needs at least 20 words for Humanly to learn your voice.");
      return;
    }
    setBuilding(true);
    try {
      const { data } = await api.post("/styles", newStyle);
      setStyles((s) => [data, ...s]);
      setNewStyle({ name: "", description: "", sample_text: "" });
      toast.success("Voice fingerprint built");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBuilding(false); }
  };

  const loadSampleFromFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/documents/extract", form, { headers: { "Content-Type": "multipart/form-data" } });
      setNewStyle((s) => ({ ...s, sample_text: data.text.slice(0, 8000), name: s.name || file.name.replace(/\.[^.]+$/, "") }));
      toast.success(`Loaded ${data.words} words from ${file.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { e.target.value = ""; }
  };

  const rebuildStyle = async (id) => {
    setRebuilding(id);
    try {
      const { data } = await api.post(`/styles/${id}/rebuild`);
      setStyles((s) => s.map((x) => (x.id === id ? data : x)));
      toast.success("Fingerprint re-measured");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setRebuilding(""); }
  };

  const delStyle = async (id) => {
    if (!window.confirm("Delete this style profile?")) return;
    await api.delete(`/styles/${id}`);
    setStyles((s) => s.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 space-y-10">
      <div>
        <div className="label-eyebrow mb-2">Profile & Settings</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Your Humanly account.</h1>
      </div>

      <section className="surface p-8">
        <h2 className="font-display text-2xl mb-6">Account</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label-eyebrow block mb-2">Name</label>
            <input
              data-testid="profile-name"
              value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-white border border-[hsl(var(--border))] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/30 focus:border-[hsl(var(--primary))]"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Email</label>
            <input value={user.email} disabled className="w-full bg-[hsl(var(--muted))] border border-[hsl(var(--border))] rounded-xl px-4 py-3 text-sm text-[hsl(var(--muted-foreground))]" />
          </div>
        </div>
        <div className="mt-6 flex items-center gap-4 flex-wrap">
          <button data-testid="profile-save" onClick={saveProfile} disabled={saving} className="btn-primary text-sm py-2 px-5">
            {saving ? "Saving…" : "Save changes"}
          </button>
          {(user.plan === "pro" || user.plan === "premium") && (
            <button
              data-testid="billing-portal-button"
              onClick={async () => {
                try {
                  const { data } = await api.post("/payments/portal", { return_url: `${window.location.origin}/profile` });
                  window.location.href = data.portal_url;
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Unable to open billing portal");
                }
              }}
              className="btn-ghost text-sm py-2 px-5 flex items-center gap-2"
            >
              Manage billing <ExternalLink size={14} />
            </button>
          )}
          <div className="text-xs text-[hsl(var(--muted-foreground))]">Plan: <span className="font-semibold capitalize">{user.plan}</span></div>
        </div>
      </section>

      <section className="surface p-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <h2 className="font-display text-2xl">Your voice fingerprints</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Paste or upload something you wrote. Humanly measures how you actually write — rhythm, punctuation
              habits, the way you open sentences — and reuses it on every rewrite.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="md:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                data-testid="style-name"
                placeholder="Style name (e.g. My blog voice)"
                value={newStyle.name} onChange={(e) => setNewStyle((s) => ({ ...s, name: e.target.value }))}
                className="bg-white border border-[hsl(var(--border))] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/30 focus:border-[hsl(var(--primary))]"
              />
              <input
                data-testid="style-description"
                placeholder="Short description (optional)"
                value={newStyle.description} onChange={(e) => setNewStyle((s) => ({ ...s, description: e.target.value }))}
                className="bg-white border border-[hsl(var(--border))] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/30 focus:border-[hsl(var(--primary))]"
              />
            </div>
            <Textarea
              data-testid="style-sample"
              placeholder="Paste your own writing here — a few paragraphs works better than a few lines…"
              value={newStyle.sample_text} onChange={(e) => setNewStyle((s) => ({ ...s, sample_text: e.target.value }))}
              className="mt-4 min-h-[140px] bg-white border-[hsl(var(--border))]"
            />
            <div className="flex items-center gap-3 mt-3">
              <button data-testid="style-upload" onClick={() => sampleFileRef.current?.click()} className="btn-ghost text-sm flex items-center gap-2">
                <Upload size={14} /> Build from a document
              </button>
              <input ref={sampleFileRef} type="file" accept=".pdf,.docx,.txt,.md" onChange={loadSampleFromFile} className="hidden" />
              <span className="text-xs text-[hsl(var(--muted-foreground))]">PDF, DOCX or TXT</span>
            </div>
          </div>
          <div className="flex flex-col justify-end">
            <button
              data-testid="style-create"
              onClick={addStyle}
              disabled={!newStyle.name.trim() || !newStyle.sample_text.trim() || building}
              className="btn-primary flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {building ? <><Loader2 size={14} className="animate-spin" /> Measuring your voice…</> : <><Plus size={14} /> Build fingerprint</>}
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {styles.length === 0 ? (
            <div className="text-sm text-[hsl(var(--muted-foreground))]">No voice fingerprints yet.</div>
          ) : styles.map((s) => (
            <div key={s.id} data-testid={`style-${s.id}`} className="border-t border-[hsl(var(--border))] pt-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2">
                    <Fingerprint size={14} className="text-[hsl(var(--primary))]" /> {s.name}
                  </div>
                  {s.description && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{s.description}</div>}
                  {s.accepted_count > 0 && (
                    <div className="text-xs text-[hsl(var(--accent))] mt-1">
                      Learned from {s.accepted_count} rewrite{s.accepted_count > 1 ? "s" : ""} you kept
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    data-testid={`style-rebuild-${s.id}`}
                    onClick={() => rebuildStyle(s.id)}
                    disabled={rebuilding === s.id}
                    className="pill disabled:opacity-60"
                    title="Re-measure this fingerprint, including everything it has learned"
                  >
                    {rebuilding === s.id ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  </button>
                  <button data-testid={`style-delete-${s.id}`} onClick={() => delStyle(s.id)} className="pill hover:bg-red-50 hover:text-red-700 hover:border-red-200">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              <VoiceFingerprint fingerprint={s.fingerprint} testId={`fingerprint-${s.id}`} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
